/**
 * SOLID Review
 * S — Good. One concern: turn the server's streams into deliveries — subscribe what the watch target
 *     names, unsubscribe what it no longer names, and hand every event to `ClientServerEvents`.
 * O — Good. A new stream is one subscription in `subscribeRoom` or `subscribeRound`.
 * L — Strong. Implements `IPortClientServerWatch` exactly.
 * I — Good. `watch`/`unwatch` and the account's pair; the subscription bookkeeping is private.
 * D — Strong. The adapters' subscription interfaces, the websocket monitor and the delivery port all
 *     arrive by constructor; nothing concrete is named.
 */
import type { IContextRequest } from '../../domain/ports/schemas/ContextRequest';
import type { IPayloadTableView } from '../../domain/ports/schemas/TableEvents';
import type { IPortClientServerWatch, IPayloadWatchTarget } from '../../application/ports/contracts';
import type { IPayloadAccountWatch } from '../../domain/ports/schemas/PaymentMoves';
import type { IAdapterApolloPayment, IEntityConsumedCredit } from '../../application/payment/ports/IAdapterApolloPayment';
import type { IPortClientServerEvents, IPayloadClientServerEvent } from '../../application/ports/ClientServerEvents.contract';
import type { IAdapterApolloGame } from '../../application/ports/IAdapterApolloGame';
import type { IAdapterApolloRoom } from '../../application/ports/IAdapterApolloRoom';
import type { IEntityTableView } from '../../application/ports/IAdapterApolloRoom';
import type { IEntityRawRoom, IEntityRoundInfo, IPayloadClaim, IEntityChallenge, IEntityRoll } from '../../application/ports/apolloShapes';

/** How long a delivery's decision may take — generous: a resync it triggers reads the server twice. */
const DELIVERY_BUDGET_MS = 30_000;

export interface IConfigDepServerWatch {
  readonly rooms: Pick<IAdapterApolloRoom,
    'subscribeToRoomUpdated' | 'subscribeToPlayerSuspended' | 'subscribeToPlayerResumed' | 'subscribeToRoomAbandoned'
    | 'subscribeToDecisionCountdown' | 'subscribeToTableView'>;
  readonly rounds: Pick<IAdapterApolloGame, 'subscribeToRoundStarted' | 'subscribeToClaimAnnounced' | 'subscribeToCupLifted' | 'subscribeToDiceRolled'>;
  /** The player's own topic — their pushed balance (FE4c). */
  readonly accounts: Pick<IAdapterApolloPayment, 'subscribeToCreditsUpdated'>;
  /** The websocket's state changes (`wsConnectionMonitor`): notified on change only, never on subscribe. */
  readonly connection: { onStateChange(handler: (state: string) => void): () => void };
  readonly events: IPortClientServerEvents;
  readonly now?: () => number;
}

/**
 * THE SERVER'S STREAMS, DELIVERED (frontend strict-ports plan, FE1, 2026-10-03). Before, ten
 * subscriptions lived in five components and hooks, each mounted and unmounted with them — ControlPanel,
 * GameSceneCore, LobbyPanel's two sync hooks, `useRoomSubscriptions`, `useTableView` — plus the
 * websocket hook in AppRoot. Now one adapter holds them all:
 *
 *   - ROOM streams for the watched room: round starts, the roster, suspensions and resumptions, the
 *     room's end, the decision countdown, the pushed table view;
 *   - ROUND streams for the watched round, on the room's topic (§12.36): claims, rolls, lifts;
 *   - the CONNECTION's state, from the first `watch` until `unwatch`;
 *   - the ACCOUNT's pushed balance (FE4c — AppRoot's `useCreditsUpdated`), for the player
 *     `watchAccount` names, table or no table.
 *
 * `watch` is IDEMPOTENT: it compares the new target with the current one and touches only the
 * subscriptions that differ, so StrictMode's double effect, or a re-render that asks again, subscribes
 * nothing twice.
 *
 * Every event goes to `ClientServerEvents.deliver` as it arrives, unparsed: the delivery socket's
 * contract is what parses it, so an event whose shape changed fails there as `CONTRACT` instead of
 * reaching a decision. The roller now also hears the claim and roll streams, which its components
 * never subscribed to. The decisions ignore those on the roller's table, as the unmounted
 * subscriptions did. It costs two subscriptions per round for one seat.
 */
export class ClientServerWatchAdapter implements IPortClientServerWatch {
  private target: IPayloadWatchTarget = { roomId: null, roundId: null };
  private roomStreams: Array<() => void> = [];
  private roundStreams: Array<() => void> = [];
  private connectionStream: (() => void) | null = null;
  private account: { readonly playerId: string; readonly stop: () => void } | null = null;
  private deliveries = 0;

  constructor(private readonly deps: IConfigDepServerWatch) {}

  async watch(_ctx: IContextRequest, target: IPayloadWatchTarget) {
    if (this.connectionStream === null) {
      this.connectionStream = this.deps.connection.onStateChange((state) => this.deliver(EVENT.connectivity(state)));
    }
    const roomChanged = target.roomId !== this.target.roomId;
    if (roomChanged) {
      this.stop(this.roomStreams);
      if (target.roomId !== null) this.roomStreams = this.subscribeRoom(target.roomId);
    }
    if (roomChanged || target.roundId !== this.target.roundId) {
      this.stop(this.roundStreams);
      if (target.roomId !== null && target.roundId !== null) this.roundStreams = this.subscribeRound(target.roomId, target.roundId);
    }
    this.target = target;
    return { success: true as const, value: undefined };
  }

  async unwatch(_ctx: IContextRequest) {
    this.stop(this.roomStreams);
    this.stop(this.roundStreams);
    this.connectionStream?.();
    this.connectionStream = null;
    this.target = { roomId: null, roundId: null };
    return { success: true as const, value: undefined };
  }

  /** Idempotent per player: the same player subscribes nothing again; another replaces the stream. */
  async watchAccount(_ctx: IContextRequest, target: IPayloadAccountWatch) {
    if (target.playerId !== this.account?.playerId) {
      this.account?.stop();
      this.account = target.playerId === null ? null : {
        playerId: target.playerId,
        stop: this.deps.accounts.subscribeToCreditsUpdated(target.playerId, (ev) => this.deliver(EVENT.creditsUpdated(ev))),
      };
    }
    return { success: true as const, value: undefined };
  }

  async unwatchAccount(_ctx: IContextRequest) {
    this.account?.stop();
    this.account = null;
    return { success: true as const, value: undefined };
  }

  private subscribeRoom(roomId: string): Array<() => void> {
    const { rooms, rounds } = this.deps;
    return [
      rounds.subscribeToRoundStarted(roomId, (round) => this.deliver(EVENT.roundStarted(round))),
      rooms.subscribeToRoomUpdated(roomId, (room) => this.deliver(EVENT.roomUpdated(room))),
      rooms.subscribeToPlayerSuspended(roomId, (ev) => this.deliver(EVENT.playerSuspended(ev))),
      rooms.subscribeToPlayerResumed(roomId, (ev) => this.deliver(EVENT.playerResumed(ev))),
      rooms.subscribeToRoomAbandoned(roomId, (ev) => this.deliver(EVENT.roomAbandoned(ev))),
      rooms.subscribeToDecisionCountdown(roomId, (ev) => this.deliver(EVENT.decisionCountdown(ev))),
      rooms.subscribeToTableView(roomId, (view) => this.deliver(EVENT.tableView(view))),
    ];
  }

  private subscribeRound(roomId: string, roundId: string): Array<() => void> {
    const { rounds } = this.deps;
    return [
      rounds.subscribeToClaimAnnounced(roundId, roomId, (claim) => this.deliver(EVENT.claimAnnounced(claim))),
      rounds.subscribeToCupLifted(roundId, roomId, (lift) => this.deliver(EVENT.cupLifted(lift))),
      rounds.subscribeToDiceRolled(roundId, roomId, (roll) => this.deliver(EVENT.diceRolled(roll))),
    ];
  }

  private stop(streams: Array<() => void>): void {
    for (const unsubscribe of streams.splice(0)) unsubscribe();
  }

  /** Delivered as it arrives, not awaited: the next event must not wait for this one's decision. */
  private deliver(event: IPayloadClientServerEvent): void {
    this.deliveries += 1;
    const now = this.deps.now ?? Date.now;
    const ctx: IContextRequest = { requestId: `delivery-${this.deliveries}`, deadlineAt: now() + DELIVERY_BUDGET_MS };
    void this.deps.events.deliver(ctx, event);
  }
}

type TPresence = { readonly playerId: string; readonly roomId: string };

/**
 * One factory per event: what the adapter heard, as the delivery's union arm. Lists are copied — the
 * adapters' are readonly, a delivery's are fresh — and nothing else is touched: the delivery socket's
 * contract parses the rest.
 */
const EVENT = {
  roundStarted: (payload: IEntityRoundInfo): IPayloadClientServerEvent => ({ type: 'roundStarted', payload }),
  roomUpdated: (room: IEntityRawRoom): IPayloadClientServerEvent => ({ type: 'roomUpdated', payload: { id: room.id, players: [...room.players] } }),
  playerSuspended: (payload: TPresence): IPayloadClientServerEvent => ({ type: 'playerSuspended', payload }),
  playerResumed: (payload: TPresence): IPayloadClientServerEvent => ({ type: 'playerResumed', payload }),
  roomAbandoned: (payload: { readonly roomId: string }): IPayloadClientServerEvent => ({ type: 'roomAbandoned', payload }),
  decisionCountdown: (payload: { readonly roundId: string; readonly challengerId: string; readonly secondsRemaining: number }): IPayloadClientServerEvent =>
    ({ type: 'decisionCountdown', payload }),
  tableView: (view: IEntityTableView): IPayloadClientServerEvent => ({ type: 'tableView', payload: tableViewOf(view) }),
  claimAnnounced: (payload: IPayloadClaim): IPayloadClientServerEvent => ({ type: 'claimAnnounced', payload }),
  cupLifted: (payload: IEntityChallenge): IPayloadClientServerEvent => ({ type: 'cupLifted', payload }),
  diceRolled: (payload: IEntityRoll): IPayloadClientServerEvent => ({ type: 'diceRolled', payload }),
  connectivity: (state: string): IPayloadClientServerEvent => ({ type: 'connectivity', payload: { state } }),
  creditsUpdated: (ev: IEntityConsumedCredit): IPayloadClientServerEvent =>
    ({ type: 'creditsUpdated', payload: { playerId: ev.playerId, newCredits: ev.newCredits } }),
};

function tableViewOf(view: IEntityTableView): IPayloadTableView {
  return {
    roomId: view.roomId,
    version: view.version,
    status: view.status,
    suspendedPlayerIds: [...view.suspendedPlayerIds],
    activeRound: view.activeRound === null ? null : { ...view.activeRound, playerLives: [...view.activeRound.playerLives] },
  };
}
